import React, { useRef, useEffect } from 'react';
import { setupCanvas } from './canvasUtils';

// ============================================
// Chapter 15  -  Time of Flight (TOF)
// ============================================

/** 15.1 – TOF Principle */
export const TOFPrinciple = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Time-of-Flight Principle', W / 2, 24);

        // Formula
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 16px Inter,sans-serif';
        ctx.fillText('t = L / (βc)     where β = v/c', W / 2, 52);

        // Diagram: Start detector → gap L → Stop detector
        const dy = 120;

        // Start detector
        ctx.fillStyle = '#3B82F6'; ctx.fillRect(60, dy - 35, 20, 70);
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('START', 70, dy - 42);

        // Stop detector
        ctx.fillStyle = '#EF4444'; ctx.fillRect(420, dy - 35, 20, 70);
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('STOP', 430, dy - 42);

        // Flight path
        ctx.setLineDash([6, 4]); ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(80, dy); ctx.lineTo(420, dy); ctx.stroke();
        ctx.setLineDash([]);

        // L arrow
        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(80, dy + 50); ctx.lineTo(420, dy + 50); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(80, dy + 46); ctx.lineTo(80, dy + 54); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(420, dy + 46); ctx.lineTo(420, dy + 54); ctx.stroke();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 14px Inter,sans-serif';
        ctx.fillText('L (known distance)', 250, dy + 45);

        // Particles with different speeds
        const particles = [
            { name: 'π (fast)', x: 340, color: '#22C55E', beta: '0.997' },
            { name: 'K (medium)', x: 280, color: '#F59E0B', beta: '0.965' },
            { name: 'p (slow)', x: 210, color: '#8B5CF6', beta: '0.878' },
        ];

        particles.forEach((p, i) => {
            const py = dy - 22 + i * 22;
            ctx.beginPath(); ctx.arc(p.x, py, 5, 0, Math.PI * 2);
            ctx.fillStyle = p.color; ctx.fill();
            ctx.strokeStyle = p.color; ctx.lineWidth = 1.5;
            ctx.beginPath(); ctx.moveTo(80, py); ctx.lineTo(p.x - 6, py); ctx.stroke();
            ctx.fillStyle = p.color; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(p.name + ' (β=' + p.beta + ')', p.x + 10, py + 4);
        });

        // Timing info
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Same momentum, different masses → different β → different arrival times', W / 2, dy + 75);

        // Example calculation
        const ey = 210;
        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(30, ey, 500, 60, 8); ctx.fill();
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 1; ctx.beginPath(); ctx.roundRect(30, ey, 500, 60, 8); ctx.stroke();
        ctx.fillStyle = '#2563EB'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Example: L = 10 m, p = 2 GeV/c', W / 2, ey + 18);
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('π: t = 33.4 ns    K: t = 34.6 ns    p: t = 38.0 ns', W / 2, ey + 38);
        ctx.fillText('Δt(K-π) ≈ 1.2 ns  -  requires sub-ns timing resolution!', W / 2, ey + 54);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-red-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 15.2 – Measuring β from TOF */
export const BetaFromTOF = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Measuring β from Time of Flight', W / 2, 24);

        // Step-by-step derivation
        const steps = [
            { step: 'Measure', eq: 'Δt = t_stop − t_start', desc: 'TDC records time', color: '#3B82F6' },
            { step: 'Calculate β', eq: 'β = L / (c · Δt)', desc: 'Known L, measured Δt', color: '#22C55E' },
            { step: 'Get γ', eq: 'γ = 1/√(1−β²)', desc: 'Lorentz factor', color: '#F59E0B' },
        ];

        steps.forEach((s, i) => {
            const x = 10 + i * 180, y = 45, w = 165, h = 80;
            ctx.fillStyle = s.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
            ctx.strokeStyle = s.color; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();
            ctx.fillStyle = s.color; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(s.step, x + w / 2, y + 18);
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 14px Inter,sans-serif';
            ctx.fillText(s.eq, x + w / 2, y + 42);
            ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif';
            ctx.fillText(s.desc, x + w / 2, y + 62);
            if (i < steps.length - 1) {
                ctx.fillStyle = '#94A3B8'; ctx.font = 'bold 16px Inter,sans-serif';
                ctx.fillText('→', x + w + 5, y + h / 2);
            }
        });

        // 1/β² vs p² plot concept
        const m = { left: 70, right: 30, top: 145, bottom: 45 };
        const pw = W - m.left - m.right, ph = H - m.top - m.bottom;

        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(m.left, m.top); ctx.lineTo(m.left, m.top + ph); ctx.lineTo(m.left + pw, m.top + ph); ctx.stroke();

        ctx.save(); ctx.translate(20, m.top + ph / 2); ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('1/β', 0, 0); ctx.restore();
        ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Momentum p (GeV/c)', m.left + pw / 2, H - 8);

        // Bands for different particles (1/β vs p)
        const bands = [
            { name: 'p', mass: 0.938, color: '#8B5CF6' },
            { name: 'K', mass: 0.494, color: '#F59E0B' },
            { name: 'π', mass: 0.140, color: '#22C55E' },
        ];

        bands.forEach(b => {
            ctx.strokeStyle = b.color; ctx.lineWidth = 2.5;
            ctx.beginPath();
            for (let px = 5; px <= pw; px++) {
                const p = 0.2 + (px / pw) * 4;
                const betaInv = Math.sqrt(1 + (b.mass * b.mass) / (p * p));
                const y = m.top + ph - ((betaInv - 1) / 0.7) * ph;
                const clampedY = Math.max(m.top, Math.min(m.top + ph, y));
                px === 5 ? ctx.moveTo(m.left + px, clampedY) : ctx.lineTo(m.left + px, clampedY);
            }
            ctx.stroke();

            // Label
            const pLabel = 0.6;
            const betaInvLabel = Math.sqrt(1 + (b.mass * b.mass) / (pLabel * pLabel));
            const lx = m.left + ((pLabel - 0.2) / 4) * pw;
            const ly = m.top + ph - ((betaInvLabel - 1) / 0.7) * ph;
            ctx.fillStyle = b.color; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(b.name, lx + 5, Math.max(m.top + 12, ly - 5));
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Bands separate at low p → TOF identifies slow particles best', m.left + pw / 2, m.top + ph - 8);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-green-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 15.3 – Mass Reconstruction from TOF */
export const MassReconstruction = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Mass Reconstruction from TOF + Momentum', W / 2, 24);

        // Derivation
        const derivSteps = [
            { eq: 'β = L/(cΔt)', label: 'From TOF' },
            { eq: 'p = γmβc', label: 'Relativistic momentum' },
            { eq: 'm² = p²(1/β² − 1)/c²', label: 'Solve for mass' },
        ];

        derivSteps.forEach((s, i) => {
            const x = 15 + i * 185, y = 45, w = 168, h = 55;
            ctx.fillStyle = ['#3B82F6', '#22C55E', '#EF4444'][i] + '10';
            ctx.beginPath(); ctx.roundRect(x, y, w, h, 8); ctx.fill();
            ctx.strokeStyle = ['#3B82F6', '#22C55E', '#EF4444'][i]; ctx.lineWidth = 1.5;
            ctx.beginPath(); ctx.roundRect(x, y, w, h, 8); ctx.stroke();

            ctx.fillStyle = ['#3B82F6', '#22C55E', '#EF4444'][i]; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(s.label, x + w / 2, y + 16);
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 14px Inter,sans-serif';
            ctx.fillText(s.eq, x + w / 2, y + 40);

            if (i < derivSteps.length - 1) {
                ctx.fillStyle = '#94A3B8'; ctx.font = 'bold 14px Inter,sans-serif';
                ctx.fillText('→', x + w + 6, y + h / 2);
            }
        });

        // Mass² histogram (conceptual)
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Reconstructed m² Distribution', W / 2, 118);

        const hx = 70, hy = 128, hw = 420, hh = 120;
        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(hx, hy); ctx.lineTo(hx, hy + hh); ctx.lineTo(hx + hw, hy + hh); ctx.stroke();

        ctx.save(); ctx.translate(30, hy + hh / 2); ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Counts', 0, 0); ctx.restore();
        ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('m² (GeV²/c⁴)', hx + hw / 2, H - 6);

        // Gaussian peaks for π, K, p
        const peaks = [
            { name: 'π', m2: 0.0196, sigma: 0.015, height: 100, color: '#22C55E' },
            { name: 'K', m2: 0.244, sigma: 0.03, height: 60, color: '#F59E0B' },
            { name: 'p', m2: 0.880, sigma: 0.06, height: 40, color: '#8B5CF6' },
        ];

        const m2Max = 1.2;
        peaks.forEach(pk => {
            ctx.strokeStyle = pk.color; ctx.lineWidth = 2.5;
            ctx.fillStyle = pk.color + '20';
            ctx.beginPath();
            for (let px = 0; px <= hw; px++) {
                const m2 = (px / hw) * m2Max;
                const val = pk.height * Math.exp(-0.5 * Math.pow((m2 - pk.m2) / pk.sigma, 2));
                const y = hy + hh - (val / 110) * hh;
                px === 0 ? ctx.moveTo(hx + px, y) : ctx.lineTo(hx + px, y);
            }
            ctx.stroke();

            // Peak label
            const peakX = hx + (pk.m2 / m2Max) * hw;
            const peakY = hy + hh - (pk.height / 110) * hh;
            ctx.fillStyle = pk.color; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(pk.name, peakX, peakY - 8);
        });
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-green-50 to-purple-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 15.4 – BL4S TOF Setup (case study) */
export const BL4STOF = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('BL4S TOF Experimental Setup', W / 2, 24);

        const dy = 100;

        // Beam pipe
        ctx.fillStyle = '#E2E8F0'; ctx.fillRect(0, dy - 8, W, 16);
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(0, dy - 8); ctx.lineTo(W, dy - 8); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(0, dy + 8); ctx.lineTo(W, dy + 8); ctx.stroke();

        ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('beam', 5, dy - 14);

        // Components along beamline
        const components = [
            { x: 80, label: 'S1\n(trigger)', w: 14, h: 50, color: '#3B82F6' },
            { x: 180, label: 'Target /\nDegrader', w: 20, h: 40, color: '#F59E0B' },
            { x: 280, label: 'S2\n(TOF start)', w: 14, h: 50, color: '#22C55E' },
            { x: 420, label: 'S3\n(TOF stop)', w: 14, h: 50, color: '#EF4444' },
        ];

        components.forEach(comp => {
            ctx.fillStyle = comp.color; ctx.fillRect(comp.x - comp.w / 2, dy - comp.h / 2, comp.w, comp.h);
            ctx.fillStyle = comp.color; ctx.font = 'bold 10px Inter,sans-serif'; ctx.textAlign = 'center';
            comp.label.split('\n').forEach((line, i) => ctx.fillText(line, comp.x, dy + comp.h / 2 + 14 + i * 12));
        });

        // L arrow between S2 and S3
        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(290, dy - 35); ctx.lineTo(415, dy - 35); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(290, dy - 38); ctx.lineTo(290, dy - 32); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(415, dy - 38); ctx.lineTo(415, dy - 32); ctx.stroke();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('L ≈ 10-15 m', 352, dy - 42);

        // DAQ box
        ctx.fillStyle = '#8B5CF6'; ctx.beginPath(); ctx.roundRect(460, dy - 30, 80, 60, 8); ctx.fill();
        ctx.fillStyle = 'white'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('TDC', 500, dy - 5); ctx.fillText('(DAQ)', 500, dy + 12);

        // Signal lines to DAQ
        ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 1; ctx.setLineDash([3, 2]);
        ctx.beginPath(); ctx.moveTo(280, dy - 25); ctx.lineTo(465, dy - 20); ctx.stroke();
        ctx.strokeStyle = '#EF4444';
        ctx.beginPath(); ctx.moveTo(420, dy - 25); ctx.lineTo(465, dy - 8); ctx.stroke();
        ctx.setLineDash([]);

        // Specs table
        const ty = 165;
        const specs = [
            { param: 'Scintillator', value: 'EJ-200 plastic, 10�-10 cm²', color: '#3B82F6' },
            { param: 'Photodetector', value: 'SiPM (Hamamatsu S13360)', color: '#22C55E' },
            { param: 'Time resolution', value: '< 200 ps (σ)', color: '#F59E0B' },
            { param: 'Momentum range', value: '0.5 – 5 GeV/c', color: '#EF4444' },
        ];

        specs.forEach((s, i) => {
            const y = ty + i * 28;
            ctx.fillStyle = i % 2 === 0 ? '#F8FAFC' : 'white';
            ctx.fillRect(50, y, 460, 26);
            ctx.fillStyle = s.color; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'right';
            ctx.fillText(s.param, 180, y + 18);
            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(s.value, 195, y + 18);
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The Z-SCANNER uses TOF to separate π/K/p in the T9 beamline at CERN', W / 2, H - 8);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-green-50 to-red-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 15.5 – TOF Resolution and Limits */
export const TOFResolution = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('TOF Resolution & Separation Power', W / 2, 24);

        // Key formula
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif';
        ctx.fillText('Δt₁₂ = (L/2cp²)(m₁² − m₂²)     n_σ = Δt₁₂ / σ_t', W / 2, 52);

        // Left: resolution factors
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Resolution Factors', 145, 78);

        const factors = [
            { name: 'Scintillator timing', impact: '100-200 ps', color: '#3B82F6' },
            { name: 'SiPM jitter', impact: '50-100 ps', color: '#22C55E' },
            { name: 'Electronics (TDC)', impact: '25-50 ps', color: '#F59E0B' },
            { name: 'Path length uncertainty', impact: 'depends on setup', color: '#8B5CF6' },
        ];

        factors.forEach((f, i) => {
            const y = 90 + i * 26;
            ctx.fillStyle = f.color; ctx.fillRect(30, y, 10, 10);
            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(f.name + ': ' + f.impact, 46, y + 10);
        });

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('σ_total = √(σ₁² + σ₂² + σ_elec²)', 40, 200);

        // Right: separation plot (n_sigma vs p)
        const m = { left: 325, right: 30, top: 75, bottom: 45 };
        const pw = W - m.left - m.right, ph = H - m.top - m.bottom;

        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(m.left, m.top); ctx.lineTo(m.left, m.top + ph); ctx.lineTo(m.left + pw, m.top + ph); ctx.stroke();

        ctx.save(); ctx.translate(310, m.top + ph / 2); ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('n_σ separation', 0, 0); ctx.restore();
        ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('p (GeV/c)', m.left + pw / 2, H - 8);

        // 3σ line
        const threeY = m.top + ph - (3 / 15) * ph;
        ctx.setLineDash([4, 3]); ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(m.left, threeY); ctx.lineTo(m.left + pw, threeY); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#EF4444'; ctx.font = '9px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText('3σ', m.left - 3, threeY + 4);

        // π/K separation curve (L=10m, σ=150ps)
        const L = 10, sigma = 0.15e-9, c_val = 3e8;
        const m_pi = 0.140, m_K = 0.494;
        const pairs = [
            { name: 'π/K', m1: m_pi, m2: m_K, color: '#22C55E' },
            { name: 'K/p', m1: m_K, m2: 0.938, color: '#F59E0B' },
        ];

        pairs.forEach(pair => {
            ctx.strokeStyle = pair.color; ctx.lineWidth = 2.5;
            ctx.beginPath();
            for (let px = 2; px <= pw; px++) {
                const p = 0.5 + (px / pw) * 4.5;
                const dt = (L / (2 * c_val * p * p)) * (pair.m2 * pair.m2 - pair.m1 * pair.m1);
                const nSigma = Math.abs(dt) / sigma;
                const y = m.top + ph - (nSigma / 15) * ph;
                const clampedY = Math.max(m.top, Math.min(m.top + ph, y));
                px === 2 ? ctx.moveTo(m.left + px, clampedY) : ctx.lineTo(m.left + px, clampedY);
            }
            ctx.stroke();

            // Label at p=1
            const pL = 1.0;
            const dtL = (L / (2 * c_val * pL * pL)) * (pair.m2 * pair.m2 - pair.m1 * pair.m1);
            const nSL = Math.abs(dtL) / sigma;
            const lx = m.left + ((pL - 0.5) / 4.5) * pw;
            const ly = m.top + ph - (nSL / 15) * ph;
            ctx.fillStyle = pair.color; ctx.font = 'bold 10px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(pair.name, lx + 5, Math.max(m.top + 10, ly - 5));
        });
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 15.10 – Alternative Timing Detectors */
export const AlternativeTimingDetectors = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Alternative Timing Detectors', W / 2, 24);

        const detectors = [
            {
                name: 'RPC', full: 'Resistive Plate\nChamber',
                res: '~50 ps', pros: 'Large area,\ncheap', cons: 'HV, gas',
                color: '#3B82F6'
            },
            {
                name: 'MRPC', full: 'Multi-gap\nRPC',
                res: '~20-50 ps', pros: 'Very good\nresolution', cons: 'Complex,\ngas system',
                color: '#22C55E'
            },
            {
                name: 'MCP-PMT', full: 'Micro-Channel\nPlate PMT',
                res: '~10-30 ps', pros: 'Excellent\nresolution', cons: 'Expensive,\nsmall area',
                color: '#F59E0B'
            },
            {
                name: 'LGAD', full: 'Low-Gain\nAvalanche Diode',
                res: '~30 ps', pros: 'Compact,\nsilicon', cons: 'Radiation\ndamage',
                color: '#8B5CF6'
            },
            {
                name: 'Cherenkov', full: 'Cherenkov\nCounter',
                res: '~100 ps', pros: 'Threshold PID,\nno minimum-p', cons: 'Requires\nradiator',
                color: '#EF4444'
            },
        ];

        detectors.forEach((d, i) => {
            const x = 8 + i * 112, y = 48, w = 102, h = 200;
            ctx.fillStyle = d.color + '08'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
            ctx.strokeStyle = d.color; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();

            ctx.fillStyle = d.color; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(d.name, x + w / 2, y + 18);

            ctx.fillStyle = '#475569'; ctx.font = '9px Inter,sans-serif';
            d.full.split('\n').forEach((line, li) => ctx.fillText(line, x + w / 2, y + 32 + li * 11));

            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif';
            ctx.fillText(d.res, x + w / 2, y + 68);

            ctx.fillStyle = '#16A34A'; ctx.font = '9px Inter,sans-serif';
            ctx.fillText('Pros:', x + w / 2, y + 88);
            d.pros.split('\n').forEach((line, li) => ctx.fillText(line, x + w / 2, y + 100 + li * 11));

            ctx.fillStyle = '#DC2626'; ctx.font = '9px Inter,sans-serif';
            ctx.fillText('Cons:', x + w / 2, y + 132);
            d.cons.split('\n').forEach((line, li) => ctx.fillText(line, x + w / 2, y + 144 + li * 11));
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Each technology trades resolution, cost, area coverage, and complexity', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-rose-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};
